import { Fragment, useCallback, useId, useState, MouseEvent } from "react";
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Checkbox,
    Chip,
    Tooltip,
    SvgIcon,
    Menu,
    MenuItem,
    Divider
} from "@mui/material";

import "./style.scss";
import { ReactComponent as CheckboxIcon } from "../../../assets/checkbox.icon.svg";
import { ReactComponent as CheckboxCheckedIcon } from "../../../assets/checkbox-checked.icon.svg";
import { ReactComponent as MoreIcon } from "../../../assets/more-vertical.icon.svg";
import {
    UserWithKBRoles,
    KBRoleAssignment
} from "../../../common/interfaces/admin/user-with-kb-roles.interface";

const MAX_VISIBLE_CHIPS = 3;

const KBChips = ({ knowledgeBases }: { knowledgeBases: KBRoleAssignment[] }) => {
    const visible = knowledgeBases.slice(0, MAX_VISIBLE_CHIPS);
    const remaining = knowledgeBases.length - MAX_VISIBLE_CHIPS;

    return (
        <div className="um-kb-chips">
            {visible.map(kb => (
                <Chip
                    key={kb.knowledgeBaseUid}
                    label={`${kb.knowledgeBaseDisplayName} (${kb.role})`}
                    size="small"
                    className="um-kb-chips__chip"
                />
            ))}
            {remaining > 0 && (
                <Tooltip
                    title={knowledgeBases
                        .slice(MAX_VISIBLE_CHIPS)
                        .map(kb => `${kb.knowledgeBaseDisplayName} (${kb.role})`)
                        .join(", ")}
                >
                    <Chip
                        label={`+${remaining} more`}
                        size="small"
                        className="um-kb-chips__chip um-kb-chips__chip--more"
                    />
                </Tooltip>
            )}
        </div>
    );
};

const RowActionsMenu = ({
    user,
    handleChangeRole,
    handleRemoveFromKB,
    handleDeleteUser,
    handleResendInvite
}: {
    user: UserWithKBRoles;
    handleChangeRole: (userUid: string, kbUid: string, newRole: string) => void;
    handleRemoveFromKB: (userUid: string, kbUid: string) => void;
    handleDeleteUser: (userUid: string) => void;
    handleResendInvite: (userUid: string) => void;
}) => {
    const [menuAnchor, setMenuAnchor] = useState<Element | null>(null);
    const menuOpen = Boolean(menuAnchor);
    const menuButtonId = useId();

    const handleMenuClick = (event: MouseEvent<HTMLElement>) => {
        setMenuAnchor(event.currentTarget);
    };

    // Close and restore focus to the kebab (Escape/backdrop and the immediate
    // actions — change role / remove / resend). The Menu has disableRestoreFocus
    // so MUI doesn't restore; we do it ourselves, deferred a frame so VoiceOver's
    // cursor follows the fresh focus event (WCAG 2.4.3). Safari also doesn't focus
    // a <button> on click, so relying on the pre-open activeElement is unreliable
    // — anchoring the restore to the kebab element itself avoids that.
    const handleMenuClose = () => {
        const trigger = menuAnchor as HTMLElement | null;
        setMenuAnchor(null);
        requestAnimationFrame(() => trigger?.focus());
    };

    // Close WITHOUT restoring focus — for Delete, which opens a confirmation
    // dialog that must keep focus; restoring to the kebab here would steal the
    // VoiceOver cursor back from the just-opened dialog.
    const closeMenuNoRestore = () => setMenuAnchor(null);

    const getOppositeRole = (role: string) => (role === "manager" ? "user" : "manager");
    const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

    return (
        <Fragment>
            <button
                type="button"
                id={menuButtonId}
                className="more-menu-icon-button"
                onClick={handleMenuClick}
                aria-label={`User actions for ${user.email}`}
                aria-haspopup="true"
                aria-expanded={menuOpen}
            >
                <SvgIcon component={MoreIcon} inheritViewBox />
            </button>
            <Menu
                anchorEl={menuAnchor}
                open={menuOpen}
                onClose={handleMenuClose}
                disableRestoreFocus
                slotProps={{
                    paper: {
                        elevation: 0,
                        className: "setting-dropdown-menu"
                    }
                }}
                MenuListProps={{ "aria-labelledby": menuButtonId }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
                {user.knowledgeBases.map(kb => (
                    <MenuItem
                        key={`role-${kb.knowledgeBaseUid}`}
                        className="setting-dropdown-menu-item"
                        onClick={() => {
                            handleChangeRole(
                                user.uid,
                                kb.knowledgeBaseUid,
                                getOppositeRole(kb.role)
                            );
                            handleMenuClose();
                        }}
                    >
                        <span className="setting-dropdown-menu-item__title">
                            {kb.knowledgeBaseDisplayName}: Switch to{" "}
                            {capitalize(getOppositeRole(kb.role))}
                        </span>
                    </MenuItem>
                ))}
                <Divider component="li" aria-hidden="true" className="um-row-menu-separator" />
                {user.knowledgeBases.map(kb => (
                    <MenuItem
                        key={`remove-${kb.knowledgeBaseUid}`}
                        className="setting-dropdown-menu-item um-row-menu-item--dangerous"
                        onClick={() => {
                            handleRemoveFromKB(user.uid, kb.knowledgeBaseUid);
                            handleMenuClose();
                        }}
                    >
                        <span className="setting-dropdown-menu-item__title">
                            Remove from {kb.knowledgeBaseDisplayName}
                        </span>
                    </MenuItem>
                ))}
                {!user.isRegistered && user.knowledgeBases.length > 0 && (
                    <Divider component="li" aria-hidden="true" className="um-row-menu-separator" />
                )}
                {!user.isRegistered && user.knowledgeBases.length > 0 && (
                    <MenuItem
                        className="setting-dropdown-menu-item"
                        onClick={() => {
                            handleResendInvite(user.uid);
                            handleMenuClose();
                        }}
                    >
                        <span className="setting-dropdown-menu-item__title">Resend Invitation</span>
                    </MenuItem>
                )}
                <Divider component="li" aria-hidden="true" className="um-row-menu-separator" />
                <MenuItem
                    className="setting-dropdown-menu-item um-row-menu-item--dangerous"
                    onClick={() => {
                        // Close the menu WITHOUT restoring focus to the kebab (the
                        // menu's restore would pull the VoiceOver cursor back off
                        // the dialog), then open the dialog on the NEXT frame. Both
                        // parts matter: opening it in the same tick the menu
                        // unmounts leaves VoiceOver silent; deferring one frame lets
                        // the menu fully unmount so the dialog is announced as a
                        // clean, separate event (WCAG 2.4.3 / 4.1.2).
                        closeMenuNoRestore();
                        requestAnimationFrame(() => handleDeleteUser(user.uid));
                    }}
                >
                    <span className="setting-dropdown-menu-item__title">Delete User</span>
                </MenuItem>
            </Menu>
        </Fragment>
    );
};

export const ActiveUsersTable = ({
    list,
    checkedItems,
    setCheckedItems,
    handleChangeRole,
    handleRemoveFromKB,
    handleDeleteUser,
    handleResendInvite
}: {
    list: UserWithKBRoles[];
    checkedItems: UserWithKBRoles[];
    setCheckedItems: (items: UserWithKBRoles[]) => void;
    handleChangeRole: (userUid: string, kbUid: string, newRole: string) => void;
    handleRemoveFromKB: (userUid: string, kbUid: string) => void;
    handleDeleteUser: (userUid: string) => void;
    handleResendInvite: (userUid: string) => void;
}) => {
    const isSelectedItem = useCallback(
        (user: UserWithKBRoles) => {
            return checkedItems.some(item => item.uid === user.uid);
        },
        [checkedItems]
    );

    const handleChangeSelect = useCallback(
        (item: UserWithKBRoles) => {
            const exists = checkedItems.find(ci => ci.uid === item.uid);
            if (exists) {
                setCheckedItems(checkedItems.filter(ci => ci.uid !== item.uid));
            } else {
                setCheckedItems([...checkedItems, item]);
            }
        },
        [checkedItems, setCheckedItems]
    );

    const handleChangeSelectAll = useCallback(() => {
        setCheckedItems(list.length === checkedItems.length ? [] : [...list]);
    }, [list, checkedItems, setCheckedItems]);

    return (
        <div className="um-table-view">
            <TableContainer>
                <Table stickyHeader className="um-table">
                    <TableHead className="um-table-header">
                        <TableRow className="um-table-header-row">
                            <TableCell className="um-table-header-cell um-table-header-cell--checkbox">
                                <span className="visually-hidden">Select</span>
                                <Checkbox
                                    checked={list.length > 0 && list.length === checkedItems.length}
                                    onChange={handleChangeSelectAll}
                                    icon={<CheckboxIcon />}
                                    checkedIcon={<CheckboxCheckedIcon />}
                                    inputProps={{ "aria-label": "Select all users" }}
                                />
                            </TableCell>
                            <TableCell className="um-table-header-cell">
                                <span className="um-table-header-cell__text">Email</span>
                            </TableCell>
                            <TableCell className="um-table-header-cell um-table-header-cell--kb">
                                <span className="um-table-header-cell__text">Assistants</span>
                            </TableCell>
                            <TableCell className="um-table-header-cell um-table-header-cell--actions">
                                <span className="um-table-header-cell__text">Action</span>
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody className="um-table-body">
                        {list.map(item => (
                            <TableRow key={item.uid} className="um-table-row">
                                <TableCell className="um-table-cell um-table-cell--checkbox">
                                    <Checkbox
                                        checked={isSelectedItem(item)}
                                        onChange={() => handleChangeSelect(item)}
                                        icon={<CheckboxIcon />}
                                        checkedIcon={<CheckboxCheckedIcon />}
                                        inputProps={{ "aria-label": `Select ${item.email}` }}
                                    />
                                </TableCell>
                                <TableCell className="um-table-cell">
                                    <span className="um-table-cell__text">
                                        {item.email}
                                        {item.role === "admin" ? " (admin)" : ""}
                                        {!item.isRegistered ? " (not registered)" : ""}
                                    </span>
                                </TableCell>
                                <TableCell className="um-table-cell um-table-cell--kb">
                                    <KBChips knowledgeBases={item.knowledgeBases} />
                                </TableCell>
                                <TableCell className="um-table-cell um-table-cell--actions">
                                    <RowActionsMenu
                                        user={item}
                                        handleChangeRole={handleChangeRole}
                                        handleRemoveFromKB={handleRemoveFromKB}
                                        handleDeleteUser={handleDeleteUser}
                                        handleResendInvite={handleResendInvite}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </div>
    );
};
